import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import {
  IconReceipt, IconLoader, IconAlert,
} from "../components/Icons";
import SearchableSelect from "../components/SearchableSelect";
import { toast } from "../lib/toast";

interface EetReceipt {
  id: string;
  order_id: string;
  order_number: string | null;
  receipt_seq: number;
  eic: string;
  premise_id: string;
  cash_register_id: string;
  total_gross: number;
  pok: string | null;
  status: string;
  error_message: string | null;
  sent_at: string | null;
  confirmed_at: string | null;
  created_at: string;
}

const STATUS_KEYS = ["pending", "sent", "offline", "error", "replayed"];
const STATUS_BADGES: Record<string, string> = {
  pending: "badge badge-yellow", sent: "badge badge-green",
  offline: "badge badge-yellow", error: "badge badge-red", replayed: "badge badge-green",
};

function formatMoney(minor: number): string {
  return new Intl.NumberFormat("cs-CZ", { style: "currency", currency: "CZK", minimumFractionDigits: 0 }).format(minor / 100);
}

export default function Eet() {
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const canResend = can(user, "eet_resend");
  const [receipts, setReceipts] = useState<EetReceipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filterStatus !== "all") params.set("status", filterStatus);
      const recs = await api.get<EetReceipt[]>(`/eet/receipts?${params}`).then(r => r.data);
      setReceipts(recs);
    } catch (e) {
      setErr(apiError(e, t("eet.loadError")));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, t]);

  useEffect(() => { load(); }, [load]);

  const resend = async (id: string) => {
    try {
      await api.post(`/eet/receipts/${id}/resend`);
      toast("ok", t("eet.resendOk"));
      load();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast("err", detail || t("eet.resendError"));
    }
  };

  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>EET</h1>
        <p>{t("eet.subtitle")}</p>
      </div>

      {err && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
          <IconAlert size={16} /> {err}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <SearchableSelect
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: "all", label: t("eet.allStatuses") },
            { value: "sent", label: t("eet.status.sent") },
            { value: "offline", label: t("eet.status.offline") },
            { value: "error", label: t("eet.status.error") },
            { value: "pending", label: t("eet.status.pending") },
            { value: "replayed", label: t("eet.status.replayed") },
          ]}
          placeholder={t("eet.allStatuses")}
          searchPlaceholder={t("eet.searchStatus")}
        />
      </div>

      {/* Receipts */}
      {receipts.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: "center" }}>
          <IconReceipt size={48} className="muted" />
          <h3 style={{ marginTop: 16, fontSize: 18, fontWeight: 600 }}>{t("eet.empty")}</h3>
          <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 8 }}>
            {t("eet.emptyHint")}
          </p>
        </div>
      ) : (
        <div className="card">
          <table>
            <thead>
              <tr><th>{t("eet.colReceipt")}</th><th>EIČ</th><th>{t("eet.colPremise")}</th><th>{t("eet.colRegister")}</th><th>{t("orders.colTotal")}</th><th>POK</th><th>{t("eet.colSent")}</th><th>{t("common.status")}</th><th></th></tr>
            </thead>
            <tbody>
              {receipts.map(r => (
                <tr key={r.id}>
                  <td className="mono" style={{ fontWeight: 600 }}>{r.order_number || "—"}<div style={{ fontSize: 11, color: "var(--muted)" }}>#{r.receipt_seq}</div></td>
                  <td className="mono" style={{ fontSize: 13 }}>{r.eic}</td>
                  <td className="mono" style={{ fontSize: 13 }}>{r.premise_id}</td>
                  <td className="mono" style={{ fontSize: 13 }}>{r.cash_register_id}</td>
                  <td style={{ fontWeight: 600 }}>{formatMoney(r.total_gross)}</td>
                  <td className="mono" style={{ fontSize: 12, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }} title={r.pok || ""}>{r.pok || "—"}</td>
                  <td style={{ fontSize: 13, color: "var(--muted)" }}>
                    {r.sent_at ? new Date(r.sent_at).toLocaleString("cs-CZ", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </td>
                  <td>
                    <span className={STATUS_BADGES[r.status] || "badge"}>{STATUS_KEYS.includes(r.status) ? t(`eet.status.${r.status}`) : r.status}</span>
                    {r.error_message && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 4, maxWidth: 200 }}>{r.error_message}</div>}
                  </td>
                  <td>
                    {(r.status === "offline" || r.status === "error") && canResend && (
                      <button className="btn btn-secondary" style={{ fontSize: 12, padding: "4px 10px" }} onClick={() => resend(r.id)}>
                        {t("eet.resend")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
